import React, { useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Dialog, DialogTitle, DialogContent, IconButton, CircularProgress, Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { fetchS3File, clearFile, selectFileBlob, selectFileLoading, selectFileError } from '../../modules/file';
import { AppDispatch } from '../../store';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs`;

interface DocumentPreviewPopupProps {
  open: boolean;
  onClose: () => void;
  fileUrl: string;
  fileType: 'image' | 'pdf' | 'other'; // 'other' for unsupported types, or can be expanded
  fileName: string;
}

const DocumentPreviewPopup: React.FC<DocumentPreviewPopupProps> = ({
  open,
  onClose,
  fileUrl,
  fileType,
  fileName,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const fileBlob = useSelector(selectFileBlob);
  const loading = useSelector(selectFileLoading);
  const error = useSelector(selectFileError);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (fileUrl && !fileUrl.startsWith('http')) {
        dispatch(fetchS3File({ filePath: fileUrl }));
      } else {
        setPreviewUrl(fileUrl);
      }
    } else {
      setPreviewUrl(null);
      dispatch(clearFile());
    }
  }, [open, fileUrl, dispatch]);

  useEffect(() => {
    if (fileBlob && open && fileUrl && !fileUrl.startsWith('http')) {
      console.log('DocumentPreviewPopup: fileBlob', fileBlob);
      let blob: Blob | null = null;

      if (fileBlob instanceof Blob) {
        blob = fileBlob;
        console.log('DocumentPreviewPopup: Blob Type:', blob.type);
        console.log('DocumentPreviewPopup: Blob Size:', blob.size);
        if (blob.type === 'application/json' || blob.type === 'text/html') {
          console.warn('DocumentPreviewPopup: Warning! Blob type is not PDF. It might be an error response.');
        }
      } else {
        // Fallback or error handling if it's not a blob (e.g. might be an error object or JSON)
        console.error('DocumentPreviewPopup: Expected Blob but got', typeof fileBlob);
        // If it's a generic object (maybe JSON from axios when it auto-parses even if we asked for blob), try to see if it has data
        return;
      }

      if (fileType === 'pdf') {
        // Explicitly set type for PDFs to ensure proper rendering
        blob = new Blob([blob], { type: 'application/pdf' });
      }

      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [fileBlob, open, fileUrl, fileType]);

  const [numPages, setNumPages] = useState<number | null>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const handleClose = () => {
    setPreviewUrl(null);
    setNumPages(null);
    dispatch(clearFile());
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {fileName}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ height: '80vh', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', overflowY: 'auto' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
            <CircularProgress />
          </Box>
        )}
        {error && !loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
        {!loading && !error && previewUrl && (
          <>
            {fileType === 'image' && (
              <img src={previewUrl} alt={fileName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            )}
            {fileType === 'pdf' && (
              previewUrl.startsWith('blob:') ? (
                <Document
                  file={previewUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={(error) => console.error('PDF Load Error:', error)}
                  loading={<CircularProgress />}
                  error={<Typography color="error">Failed to load PDF file.</Typography>}
                >
                  {Array.from(new Array(numPages), (el, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      width={600}
                      className="mb-4"
                    />
                  ))}
                </Document>
              ) : (
                <iframe src={`https://docs.google.com/gview?url=${encodeURIComponent(previewUrl)}&embedded=true`} title={fileName} width="100%" height="100%" style={{ border: 'none' }} />
              )
            )}
            {fileType === 'other' && (
              <p>Preview not available for this file type. Please download to view.</p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentPreviewPopup;
